import React, { useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlayCircleIcon } from '../../Icons';
import { useSwipeGesture } from '../../../hooks/useSwipeGesture';
import { useBodyScrollLock } from '../identity/useBodyScrollLock';

interface PropertyFullscreenViewerProps {
  isOpen: boolean;
  onClose: () => void;
  validImages: string[];
  mainVideo: { embedUrl: string } | null;
  activeIdx: number;
  setActiveIdx: (idx: number) => void;
}

export const PropertyFullscreenViewer: React.FC<PropertyFullscreenViewerProps> = ({
  isOpen,
  onClose,
  validImages,
  mainVideo,
  activeIdx,
  setActiveIdx,
}) => {
  useBodyScrollLock(isOpen);
  const totalItems = validImages.length + (mainVideo ? 1 : 0);

  const handleNext = () => {
    if (totalItems <= 1) return;
    setActiveIdx(activeIdx === validImages.length - 1 ? (mainVideo ? -1 : 0) : activeIdx === -1 ? 0 : activeIdx + 1);
  };

  const handlePrev = () => {
    if (totalItems <= 1) return;
    setActiveIdx(activeIdx === -1 ? validImages.length - 1 : activeIdx === 0 ? (mainVideo ? -1 : validImages.length - 1) : activeIdx - 1);
  };

  const { containerProps, dragOffset, isDragging } = useSwipeGesture({ onSwipeLeft: handleNext, onSwipeRight: handlePrev });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIdx, totalItems]);

  if (!isOpen) return null;

  const isVideo = activeIdx === -1 && mainVideo;
  const currentNum = activeIdx === -1 ? 1 : activeIdx + 1;

  return (
    <div 
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex flex-col justify-between p-3 sm:p-5 pb-5 sm:pb-7 overflow-hidden select-none animate-fade-in"
      onClick={onClose}
    >
      <div className="flex items-center justify-between z-10 w-full max-w-7xl mx-auto shrink-0 py-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-white text-xs sm:text-sm font-semibold px-3 py-1 bg-white/10 rounded-full border border-white/10">
          {isVideo ? 'Video' : `Imagen ${currentNum} de ${validImages.length}`}
        </div>
        <button
          onClick={onClose}
          className="p-2 sm:p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all active:scale-95"
          aria-label="Cerrar pantalla completa"
          title="Cerrar (Esc)"
        >
          <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      <div 
        className="relative flex-1 min-h-0 w-full flex items-center justify-center my-1 sm:my-2 overflow-hidden cursor-grab active:cursor-grabbing touch-pan-y" 
        onClick={(e) => e.stopPropagation()}
        {...containerProps}
      >
        {isVideo ? (
          <div className="w-full max-w-4xl aspect-video">
            <iframe className="w-full h-full rounded-xl shadow-2xl" src={mainVideo.embedUrl} frameBorder="0" allowFullScreen title="Video" />
          </div>
        ) : validImages[activeIdx] ? (
          <img
            src={validImages[activeIdx]}
            alt={`Imagen ${currentNum}`}
            draggable={false}
            style={{
              transform: `translateX(${dragOffset}px)`,
              transition: isDragging ? 'none' : 'transform 0.22s ease-out',
            }}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl pointer-events-none select-none"
          />
        ) : null}

        {totalItems > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
              className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-black/60 hover:bg-black/90 text-white transition-all backdrop-blur-md active:scale-90"
              aria-label="Anterior"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-black/60 hover:bg-black/90 text-white transition-all backdrop-blur-md active:scale-90"
              aria-label="Siguiente"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {totalItems > 1 && (
        <div className="flex items-center justify-center gap-1.5 pt-2 pb-1 overflow-x-auto max-w-full custom-scrollbar z-10 shrink-0" onClick={(e) => e.stopPropagation()}>
          {mainVideo && (
            <button
              onClick={() => setActiveIdx(-1)}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 transition-all ${
                activeIdx === -1 ? 'bg-sky-500 text-white shadow' : 'bg-white/20 text-gray-300 hover:bg-white/30'
              }`}
            >
              <PlayCircleIcon className="w-3.5 h-3.5" /> Video
            </button>
          )}
          {validImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              title={`Ir a imagen ${i + 1}`}
              className={`transition-all rounded-full ${
                activeIdx === i ? 'w-6 h-2 bg-sky-500' : 'w-2 h-2 bg-white/30 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
